"use client";

import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '../lib/utils';

/**
 * Sections of one page (Radix Tabs: arrow keys move between tabs, Home/End
 * jump, the panel is labelled by its tab). Underlined by default; a list that
 * does not fit scrolls sideways and never wraps. `segmented` is for a small
 * set of mutually exclusive views of the same list.
 */
const Tabs = TabsPrimitive.Root;

type TabsListProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
  variant?: 'underline' | 'segmented';
};

const TabsListVariant = React.createContext<'underline' | 'segmented'>('underline');

const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, TabsListProps>(
  ({ className, variant = 'underline', ...props }, ref) => (
    <TabsListVariant.Provider value={variant}>
      <TabsPrimitive.List
        ref={ref}
        className={cn(
          variant === 'underline'
            ? 'flex max-w-full flex-nowrap items-stretch gap-5 overflow-x-auto overscroll-x-contain border-b border-border scrollbar-hide'
            : 'inline-flex max-w-full flex-nowrap items-center gap-0.5 overflow-x-auto rounded-md bg-muted p-0.5 scrollbar-hide',
          className
        )}
        {...props}
      />
    </TabsListVariant.Provider>
  )
);

TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const variant = React.useContext(TabsListVariant);
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'inline-flex shrink-0 select-none items-center gap-1.5 whitespace-nowrap text-sm font-medium text-muted-foreground transition-colors duration-[var(--duration-fast)]',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        'disabled:pointer-events-none disabled:text-disabled',
        variant === 'underline'
          ? '-mb-px border-b-2 border-transparent pb-2.5 pt-2 hover:text-foreground data-[state=active]:border-accent data-[state=active]:font-semibold data-[state=active]:text-selected-foreground pointer-coarse:min-h-11'
          : 'h-7 rounded-[0.3125rem] px-2.5 hover:text-foreground data-[state=active]:bg-surface data-[state=active]:text-foreground data-[state=active]:shadow-xs pointer-coarse:h-10',
        className
      )}
      {...props}
    />
  );
});

TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn('mt-4 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring', className)}
    {...props}
  />
));

TabsContent.displayName = TabsPrimitive.Content.displayName;

/**
 * Underlined section navigation made of links (each tab is its own address).
 * Mark the current one with `aria-current="page"` (NavLink does this).
 */
const TabNav = React.forwardRef<HTMLElement, React.ComponentPropsWithoutRef<'nav'>>(
  ({ className, children, ...props }, ref) => (
    <nav ref={ref} className={cn('max-w-full', className)} {...props}>
      <ul className="flex flex-nowrap items-stretch gap-5 overflow-x-auto overscroll-x-contain border-b border-border scrollbar-hide">
        {children}
      </ul>
    </nav>
  )
);

TabNav.displayName = 'TabNav';

/** The classes of one TabNav link, for NavLink/Link. */
const tabNavLinkClassName =
  'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap -mb-px border-b-2 border-transparent pb-2.5 pt-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring aria-[current=page]:border-accent aria-[current=page]:font-semibold aria-[current=page]:text-selected-foreground pointer-coarse:min-h-11';

export { Tabs, TabsList, TabsTrigger, TabsContent, TabNav, tabNavLinkClassName };
